import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 16px;
  background-color: white;
  color: #757575;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin: 16px 0;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 10px;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  font-size: 13px;
  margin-top: 8px;
  text-align: center;
`;

const GoogleLoginButton: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the Google login URL from our backend
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/url`);
      
      if (!res.ok) {
        throw new Error('Failed to get Google login URL');
      }
      
      const data = await res.json();
      
      // Store current URL to return after auth (optional)
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        sessionStorage.setItem('auth_redirect', currentPath);
      }
      
      // Redirect user to Google consent
      window.location.href = data.url;
    } catch (err) {
      console.error('Failed to initiate Google login', err);
      setError(err instanceof Error ? err.message : 'Failed to connect to Google');
      setLoading(false);
    }
  };

  return (
    <>
      <GoogleButton 
        onClick={handleLogin}
        disabled={loading}
        aria-label="Sign in with Google"
        type="button"
      >
        {loading ? (
          'Logging in...'
        ) : (
          <>
            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            {t('auth.continueWithGoogle')}
          </>
        )}
      </GoogleButton>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
  );
};

export default GoogleLoginButton;